import PropTypes from 'prop-types';
import React from 'react';
import { Modal, Button, Select, Form, Section, Counter, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../../utils/validators';

import Styles from './modalSalesEndHourSessions.module.css';

const formatFormForSubmit = form => {
    if ([0, 1].includes(form.period)) {
        return String(form.sales_end_hour);
    }

    return String(form.sales_end_hour * -1);
};

export default function ModalSalesEndHourSessions({ event, organization, settings, showModal, onSuccess, onCloseModal }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const validateSalesEndHour = React.useCallback((name, date, form) => {
        if (form.sales_end_hour === null) {
            return t('ticket.rates.modalSalesEndHourSessions.salesEndHourMissing');
        }
        return true;
    }, []);

    const [formSettings, setFormSettings, setFormSettingsObject, formErrors, settingsValidate] = useForm(
        {
            period: -1,
            sales_end_hour: 1
        },
        {
            sales_end_hour: validateSalesEndHour
        }
    );

    React.useEffect(() => {
        const salesEndHour = settings.values.sales_end_hour_relative;

        setFormSettingsObject({
            period: Math.sign(salesEndHour),
            sales_end_hour: Math.abs(salesEndHour)
        });
    }, [settings, setFormSettingsObject]);

    const onChangePeriod = React.useCallback(
        value => {
            let _form = { period: value, sales_end_hour: formSettings.sales_end_hour ? formSettings.sales_end_hour : 1 };
            if (value === 0) {
                _form.sales_end_hour = 0;
            }

            setFormSettingsObject(_form);
        },
        [formSettings, event, setFormSettingsObject]
    );

    const onChangeSalesEndHour = React.useCallback(
        (event, value) => {
            setFormSettings('sales_end_hour', value);
        },
        [setFormSettings]
    );

    const handleSubmit = React.useCallback(() => {
        if (!settingsValidate()) {
            return;
        }

        setIsSubmitting(true);
        settings.values.sales_end_hour_relative = formatFormForSubmit(formSettings);
        settings
            .save({
                organizationId: organization.id,
                eventId: event.id
            })
            .then(() => {
                onSuccess();
                onCloseModal();
                toast.success(t('ticket.rates.modalSalesEndHourSessions.success'));
            })
            .catch(e => {
                toast.error(t('ticket.rates.modalSalesEndHourSessions.error'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formSettings, settingsValidate, onCloseModal, onSuccess, event, organization, settings]);

    return (
        <Modal
            open={showModal}
            onClose={onCloseModal}
            title={t('ticket.rates.modalSalesEndHourSessions.title')}
            subtitle={t('ticket.rates.modalSalesEndHourSessions.subtitle')}
            allowExternalClosing={false}
            submitButton={<Button primary isLoading={isSubmitting} onClick={handleSubmit} label={t('ticket.common.action.save')} />}
            cancelButton={<Button onClick={() => onCloseModal()} label={t(`ticket.common.action.cancel`)} />}
        >
            <Form>
                <Form.Field>
                    <Form.Label>{t(`ticket.rates.modalSalesEndHourSessions.periodLabel`)}</Form.Label>
                    <Select
                        onChange={onChangePeriod}
                        options={[
                            { text: t(`ticket.rates.modalSalesEndHourSessions.beginningSession`), value: 0 },
                            { text: t(`ticket.rates.modalSalesEndHourSessions.beforeBeginningSession`), value: -1 },
                            { text: t(`ticket.rates.modalSalesEndHourSessions.afterBeginningSession`), value: 1 }
                        ]}
                        value={formSettings.period}
                    />
                </Form.Field>

                {formSettings.period !== 0 && (
                    <Section className={Styles['section-counter']}>
                        <Form.Label>{t(`ticket.rates.modalSalesEndHourSessions.${formSettings.period === 1 ? 'salesEndHourAfterLabel' : 'salesEndHourBeforeLabel'}`)}</Form.Label>
                        <Counter placeholder={0} onChange={onChangeSalesEndHour} value={formSettings.sales_end_hour} max={72} min={1} />
                        {formErrors['sales_end_hour'] && <Form.FieldErrors errors={[formErrors['sales_end_hour'] || null]} />}
                    </Section>
                )}
            </Form>
        </Modal>
    );
}

ModalSalesEndHourSessions.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    showModal: PropTypes.bool.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};
